import { useState } from "react";
import {
  Alert, Box, Button, Chip, MenuItem, Paper, Stack, TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { transferOrdersApi, type TransferOrderListDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { PageHeader } from "@/components/PageHeader";
import { StatusPill, statusTone } from "@/components/StatusPill";
import { formatDate } from "@/shared/datetime";

const STATUS = ["", "Draft", "Submitted", "Approved", "Posted", "Cancelled"];
const STATUS_LABEL: Record<string, string> = {
  Draft: "Draft", Submitted: "Submitted", Approved: "Approved",
  Posted: "Dispatched", Cancelled: "Cancelled",
};

/**
 * UC-M2-TR-01 — Transfer orders (inter-branch). Status "Posted" is rendered as "Dispatched"
 * per STATE_MACHINES §6. Non-zero "In transit" lights up the warning chip.
 */
export function TransferOrdersPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const canCreate = useAuth(s => s.hasPermission("warehouse.transfer.create"));

  const query = useQuery({
    queryKey: ["transfer-orders", status, pagination.page, pagination.pageSize],
    queryFn: () => transferOrdersApi.list({
      status: status || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const columns: GridColDef<TransferOrderListDto>[] = [
    { field: "documentNo", headerName: "Transfer #", width: 170 },
    { field: "documentDate", headerName: "Date", width: 110,
      valueFormatter: (v) => v ? formatDate(v as string) : "" },
    { field: "sourceWarehouseName", headerName: "From", flex: 1, minWidth: 160 },
    { field: "destWarehouseName", headerName: "To", flex: 1, minWidth: 160 },
    { field: "status", headerName: "Status", width: 130,
      renderCell: (p) => (
        <StatusPill
          label={STATUS_LABEL[p.value as string] ?? (p.value as string)}
          tone={statusTone(p.value as string)} />
      ) },
    { field: "lineCount", headerName: "Lines", width: 80, type: "number" },
    { field: "totalRequested", headerName: "Requested", width: 110, type: "number" },
    { field: "totalDispatched", headerName: "Dispatched", width: 110, type: "number" },
    { field: "totalReceived", headerName: "Received", width: 110, type: "number" },
    { field: "inTransitQty", headerName: "In transit", width: 110, type: "number",
      renderCell: (p) => Number(p.value) > 0 ? (
        <Chip size="small" color="warning" label={String(p.value)} />
      ) : <>{p.value as number}</> },
  ];

  return (
    <Box>
      <PageHeader
        breadcrumbs={["Warehousing", "Transfers"]}
        title="Inter-branch transfers"
        actions={
          canCreate ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate("/warehouse/transfers/new")}>
              New transfer
            </Button>
          ) : undefined
        }
      />
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}
            size="small" sx={{ minWidth: 160 }}>
            {STATUS.map(s => <MenuItem key={s} value={s}>{s ? (STATUS_LABEL[s] ?? s) : "All"}</MenuItem>)}
          </TextField>
        </Stack>
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []} columns={columns} loading={query.isLoading}
            paginationMode="server" rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination} onPaginationModelChange={setPagination}
            pageSizeOptions={[10, 25, 50, 100]} disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowClick={(p) => navigate(`/warehouse/transfers/${p.id}`)}
            sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
          />
        </Box>
      </Paper>
    </Box>
  );
}
